import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Box, Typography, CircularProgress, Alert } from "@mui/material";
import { useAuth } from "../../context/AuthContext";
import { hasPermission, hasBusinessAccess, hasSystemAccess } from "../../utils/permissions";

/**
 * Componente de ruta protegida con verificación de permisos
 */
const ProtectedRoute = ({ 
    children, 
    requiredPermission = null,
    requiredAccess = null, // "business", "system", "audit"
    fallbackPath = "/login",
    showLoading = true,
    showError = true
}) => {
    const { currentUser, userProfile, loading } = useAuth();
    const location = useLocation();

    // Mostrar loading mientras se verifica autenticación
    if (loading && showLoading) {
        return (
            <Box 
                display="flex" 
                flexDirection="column"
                alignItems="center" 
                justifyContent="center" 
                minHeight="50vh"
            >
                <CircularProgress size={60} />
                <Typography variant="h6" sx={{ mt: 2 }}>
                    Verificando permisos...
                </Typography>
            </Box>
        );
    }

    // Si no hay usuario, redirigir al login
    if (!currentUser) {
        return <Navigate to={fallbackPath} state={{ from: location }} replace />;
    }

    // Verificar permiso específico si se requiere
    if (requiredPermission && !hasPermission(userProfile, requiredPermission)) {
        if (showError) {
            return (
                <Box sx={{ p: 3 }}>
                    <Alert severity="error" sx={{ mb: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            Acceso Denegado
                        </Typography>
                        <Typography variant="body2">
                            No tienes permisos para acceder a esta sección. 
                            Permiso requerido: {requiredPermission}
                        </Typography>
                    </Alert>
                </Box>
            );
        }
        return <Navigate to="/dashboard" replace />;
    }

    // Verificar tipo de acceso si se requiere
    if (requiredAccess) {
        let hasAccess = false;
        
        switch (requiredAccess) {
        case "business":
            hasAccess = hasBusinessAccess(userProfile);
            break;
        case "system":
            hasAccess = hasSystemAccess(userProfile);
            break;
        case "audit":
            hasAccess = hasPermission(userProfile, "audit:view_logs");
            break;
        default:
            hasAccess = true;
        }

        if (!hasAccess) {
            if (showError) {
                return (
                    <Box sx={{ p: 3 }}>
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            <Typography variant="h6" gutterBottom>
                                Acceso Restringido
                            </Typography>
                            <Typography variant="body2">
                                Tu rol actual no tiene acceso a esta sección. 
                                Acceso requerido: {requiredAccess}
                            </Typography>
                        </Alert>
                    </Box>
                );
            }
            return <Navigate to="/dashboard" replace />;
        }
    }

    // Si pasa todas las verificaciones, mostrar el contenido
    return children;
};

/**
 * Componente específico para rutas de admin de negocio
 */
export const BusinessAdminRoute = ({ children, ...props }) => (
    <ProtectedRoute requiredAccess="business" {...props}>
        {children}
    </ProtectedRoute>
);

/**
 * Componente específico para rutas de admin de sistema
 */
export const SystemAdminRoute = ({ children, ...props }) => (
    <ProtectedRoute requiredAccess="system" {...props}>
        {children}
    </ProtectedRoute>
);

/**
 * Componente específico para rutas de auditoría
 */
export const AuditRoute = ({ children, ...props }) => (
    <ProtectedRoute requiredAccess="audit" {...props}>
        {children}
    </ProtectedRoute>
);

/**
 * Componente para rutas que requieren permiso específico
 */
export const PermissionRoute = ({ permission, children, ...props }) => (
    <ProtectedRoute requiredPermission={permission} {...props}>
        {children}
    </ProtectedRoute>
);

export default ProtectedRoute; 